import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, MessageSquare, Search, ArrowRight, Sparkles, Clock, HandCoins, MapPin, PawPrint, Star, X } from "lucide-react";
import Seo from "@/components/Seo";
import { Btn } from "@/components/Btn";
import Icon from "@/components/Icon";
import LeadForm from "@/components/LeadForm";
import GalleryGrid from "@/components/GalleryGrid";
import { HOME_GALLERY } from "@/lib/gallery";
import { Reveal, Stagger, item } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BRAND, SLOGANS, CTA } from "@/lib/brand";
import { TOP_SERVICES, servicePath } from "@/lib/services";
import { CITIES, cityPath } from "@/lib/cities";

const heroLines = ["One call", "and it", "skedaddles."];

const WHY = [
  { t: "Fast", d: "We move quick! Same day and next day pickups are kind of our whole personality.", icon: Clock },
  { t: "Friendly", d: "Polite crews, clean trucks and zero pressure. We treat your place like our own.", icon: Sparkles },
  { t: "Upfront", d: "You hear the plan before we lift a thing. No sneaky fees, no surprises, ever.", icon: HandCoins },
  { t: "Local", d: "Born and based in the Lowcountry. We know these roads, marshes and neighborhoods.", icon: MapPin },
];

const STEPS = [
  { n: "01", t: "Send it over", d: "Call, text or tap the quote button and tell us what needs to go. Snap a photo if you like!" },
  { n: "02", t: "Pick a time", d: "We give you a clear arrival window, often same day. Easy peasy." },
  { n: "03", t: "Poof. Gone.", d: "Our crew loads it, sweeps up and skedaddles. You get your space back." },
];

const FAQS = [
  { q: "What kind of junk do you haul?", a: "Almost anything that is not hazardous! Furniture, mattresses, appliances, hot tubs, garage and estate cleanouts, yard debris, remodel debris and more. If it is in the way, we will get it out." },
  { q: "How fast can you come out?", a: "Fast is our middle name! We offer same day and next day pickups across the Lowcountry whenever our schedule allows." },
  { q: "What areas do you serve?", a: "We cover Charleston, Berkeley and Dorchester counties, from downtown Charleston out to Summerville, Moncks Corner, the islands and everywhere between." },
  { q: "How does pricing work?", a: "Every job is priced upfront based on how much space your stuff takes up in our truck. You always hear the price before we start. Just reach out for a free quote!" },
  { q: "Do you offer free quotes?", a: "Yes! Quotes are always free and there is never any pressure. Tap Get My Free Quote or text us a photo." },
  { q: "Is there anything you cannot take?", a: "A short list of hazardous items like chemicals, propane, ammo, asbestos, medical waste and liquids. Check our What We Cannot Haul page for the friendly rundown." },
  { q: "Do you handle heavy material?", a: "We sure do. Concrete, tile, brick and dirt are no problem with our heavy material hauling service." },
  { q: "How do I pay?", a: "Easy! We take cash, Zelle, Venmo, Cash App and check. We will confirm the details when we set up your pickup." },
];

export default function Home() {
  const [formOpen, setFormOpen] = useState(false);
  return (
    <>
      <Seo path="/" description="Fast, friendly junk removal in Charleston, SC and across Berkeley and Dorchester counties. Call or text 843-518-0021 for a free quote today!" />

      {/* 1. HERO */}
      <section className="relative overflow-hidden border-b-2 border-charcoal bg-charcoal md:bg-transparent">
        <div className="absolute inset-0">
          <img src="/hero-real.webp" alt="Skedaddle wrapped junk removal trailer towed by a Toyota Tundra on the Ravenel Bridge in Charleston SC" width="1264" height="848" fetchPriority="high" decoding="async" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-charcoal/50 md:bg-gradient-to-r md:from-charcoal/88 md:via-charcoal/40 md:to-transparent" />
          <div className="absolute -bottom-32 -left-32 w-[38rem] h-[38rem] rounded-full bg-coral/20 blur-[130px]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 min-h-[calc(100svh-4rem)] md:min-h-0 content-center pt-6 pb-24 md:pt-20 md:pb-24 grid md:grid-cols-2 gap-6 md:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-sunny border-2 border-charcoal rounded-full px-4 py-1.5 shadow-brutsm font-body font-bold text-sm mb-4 md:mb-6 text-charcoal">
              <span className="w-2.5 h-2.5 rounded-full bg-coral animate-pulse" /> Serving the Lowcountry, SC
            </div>
            <h1 className="font-heading font-bold text-xl md:text-3xl tracking-tight text-cream drop-shadow-[2px_2px_0_#182A2E] mb-2 md:mb-3" data-testid="hero-h1">Junk Removal in Charleston, SC</h1>
            <motion.p initial="hidden" animate="show" data-testid="hero-tagline"
              variants={{ show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } } }}
              className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.08] md:leading-[1.14] text-cream drop-shadow-[3px_3px_0_#182A2E]">
              {heroLines.map((line, i) => (
                <span key={i} className="block py-[0.06em]">
                  <motion.span className="block"
                    variants={{ hidden: { y: 40, opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}>
                    {i === 2 ? <span className="inline-block bg-sunny text-charcoal rounded-2xl px-4 pb-1 shadow-[0_0_45px_rgba(255,197,61,0.9)]">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-3 md:mt-5 font-body font-bold text-base md:text-xl text-cream drop-shadow-[2px_2px_0_#182A2E] max-w-md">
              Fast, friendly junk hauling for homes and businesses. You point, we haul, it skedaddles!
            </motion.p>
            <div className="mt-4 md:mt-7 hidden md:flex flex-wrap items-center gap-2 md:gap-3 font-body">
              <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 bg-white/10 border border-cream/30 rounded-full px-4 py-2 text-cream font-bold hover:bg-white/20 transition-colors"><Phone className="w-4 h-4" /> {BRAND.phoneDisplay}</a>
              <a href={BRAND.smsPhotoHref} data-testid="hero-text-number" className="inline-flex items-center gap-2 bg-teal text-cream border-2 border-charcoal rounded-full px-4 py-2 font-bold shadow-brutsm hover:bg-teal/90 transition-colors"><MessageSquare className="w-4 h-4" /> Text us {BRAND.phoneDisplay}</a>
              <span className="hidden md:inline-flex items-center gap-2 bg-white/10 border border-cream/30 rounded-full px-4 py-2 text-cream/90 text-sm"><Clock className="w-4 h-4" /> {BRAND.hours}</span>
            </div>
          </div>

          <div className="w-full max-w-md md:justify-self-end md:-mt-12 lg:-mt-16">
            <AnimatePresence mode="wait">
              {!formOpen ? (
                <motion.div key="cta" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }} className="flex flex-col items-start gap-3 md:gap-4">
                  <button onClick={() => setFormOpen(true)} data-testid="hero-open-form"
                    className="group inline-flex items-center gap-3 bg-coral text-cream font-heading font-bold text-xl md:text-2xl rounded-full px-7 py-4 md:px-8 md:py-5 border-2 border-charcoal shadow-[0_22px_55px_-15px_rgba(255,90,60,0.75)] hover:scale-[1.03] transition-transform">
                    Get My Free Quote <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <span className="font-body font-bold text-cream text-sm bg-charcoal/40 rounded-full px-4 py-1.5 backdrop-blur-sm drop-shadow-[2px_2px_0_#182A2E]">Free and fast. Tap to tell us what needs to go!</span>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.4 }}
                  className="bg-white border border-charcoal/15 rounded-[2rem] p-5 md:p-7 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.6),0_0_80px_-10px_rgba(255,197,61,0.45)]" data-testid="hero-quote-form">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal">Get My Free Quote</h2>
                    <button onClick={() => setFormOpen(false)} data-testid="hero-close-form" aria-label="Close" className="text-charcoal/50 hover:text-charcoal"><X className="w-6 h-6" /></button>
                  </div>
                  <p className="font-body text-charcoal/60 text-sm mb-4">Tell us what needs to go and we will get right back to you!</p>
                  <LeadForm compact />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>


      {/* 2. SERVICE CHIPS */}
      <section className="bg-white border-b-2 border-charcoal py-5" data-testid="service-chips">
        <Marquee speed={28} gradient={false} autoFill pauseOnHover>
          {TOP_SERVICES.map((s) => (
            <Link key={s.slug} to={servicePath(s)}
              className="mx-1.5 inline-flex items-center gap-2 bg-white border-2 border-charcoal rounded-full px-4 py-2 font-body font-bold text-sm shadow-brutsm hover:bg-sunny transition-colors whitespace-nowrap">
              <Icon name={s.icon} className="w-4 h-4" /> {s.title}
            </Link>
          ))}
        </Marquee>
      </section>

      {/* 3. CTA BAND / MARQUEE */}
      <section className="bg-coral border-b-2 border-charcoal py-4 md:py-5">
        <Marquee speed={40} gradient={false} autoFill>
          {SLOGANS.map((s, i) => (
            <span key={i} className="font-heading font-bold text-2xl md:text-4xl text-cream mx-6 flex items-center gap-6">
              {s} <Star className="w-6 h-6 fill-sunny text-sunny" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* 4. WHAT WE HAUL */}
      <section className="bg-cream py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal>
            <p className="font-heading font-semibold text-teal text-lg">What we haul</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1 max-w-2xl">If it is in the way, we will make it skedaddle!</h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TOP_SERVICES.map((s, i) => (
              <motion.div key={s.slug} variants={item} className={i >= 3 ? "hidden sm:block" : ""}>
                <Link to={servicePath(s)} data-testid={`haul-card-${s.slug}`}
                  className="group block h-full bg-white border border-charcoal/15 rounded-3xl p-5 sm:p-7 shadow-brut hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(255,90,60,0.45)] transition-[transform,box-shadow] duration-200">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl border-2 border-charcoal bg-sunny flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-teal group-hover:text-cream transition-colors">
                    <Icon name={s.icon} className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <h3 className="font-heading font-bold text-xl sm:text-2xl">{s.title}</h3>
                  <p className="mt-2 font-body text-charcoal/70 text-sm sm:text-base">{s.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-body font-bold text-coral">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
          <div className="mt-6 sm:hidden">
            <Btn to="/services" variant="primary" size="md" className="w-full" data-testid="haul-see-all">See all services <ArrowRight className="w-5 h-5" /></Btn>
          </div>
        </div>
      </section>

      {/* 5. WHY US (numbered manifesto) */}
      <section className="bg-charcoal text-cream py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal>
            <p className="font-heading font-semibold text-sunny text-lg">Why Skedaddle</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">Four reasons neighbors call us first.</h2>
          </Reveal>
          <div className="mt-14 space-y-2">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.05}>
                <div className="group grid md:grid-cols-[auto,1fr,auto] items-center gap-5 py-6 border-t border-cream/15">
                  <span className="font-heading font-bold text-5xl md:text-7xl text-cream/25 group-hover:text-coral transition-colors">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-heading font-bold text-3xl md:text-4xl">{w.t}</h3>
                    <p className="mt-1 font-body text-cream/70 max-w-xl">{w.d}</p>
                  </div>
                  <w.icon className="hidden md:block w-12 h-12 text-teal" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-cream py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="font-heading font-semibold text-teal text-lg">How it works</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">Three steps. Junk gone.</h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <motion.div key={s.n} variants={item} className="bg-white border-2 border-charcoal rounded-3xl p-8 shadow-brut relative">
                <span className="absolute -top-5 -left-3 font-heading font-bold text-6xl text-coral">{s.n}</span>
                <h3 className="font-heading font-bold text-2xl mt-6">{s.t}</h3>
                <p className="mt-2 font-body text-charcoal/70">{s.d}</p>
              </motion.div>
            ))}
          </Stagger>
          <div className="mt-12 text-center">
            <Btn to="/contact" variant="primary">{CTA.quote}</Btn>
          </div>
        </div>
      </section>

      {/* 6b. GALLERY */}
      <section className="bg-charcoal text-cream py-20 md:py-28 border-b-2 border-charcoal" data-testid="home-gallery">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="font-heading font-semibold text-sunny text-lg">Our work</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">Real jobs. Real junk. Gone.</h2>
            <p className="mt-3 font-body text-cream/75">Garages, sheds, curbside piles and whole house cleanouts across the Lowcountry.</p>
          </Reveal>
          <GalleryGrid items={HOME_GALLERY} className="mt-12" />
          <div className="mt-12 text-center">
            <Btn to="/gallery" variant="accent" data-testid="home-gallery-more">See the full gallery</Btn>
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="bg-white py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal>
            <p className="font-heading font-semibold text-teal text-lg">Service areas</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">All across the Lowcountry.</h2>
            <p className="mt-3 font-body text-charcoal/70 max-w-xl">From downtown Charleston to Summerville, Moncks Corner and the islands. Find your town below!</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/service-areas" className="mt-8 flex items-center gap-3 bg-cream border-2 border-charcoal rounded-full px-5 py-4 shadow-brut max-w-md hover:bg-sunny transition-colors" data-testid="areas-search-link">
              <Search className="w-5 h-5" /> <span className="font-body font-semibold">Search all 30 service areas</span>
            </Link>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="home-city-grid">
            {CITIES.slice(0, 14).map((c) => (
              <Link key={c.slug} to={cityPath(c)}
                className="inline-flex items-center gap-2 bg-white border-2 border-charcoal rounded-full px-4 py-2 font-body font-semibold text-sm shadow-brutsm hover:bg-teal hover:text-cream transition-colors">
                <MapPin className="w-4 h-4" /> {c.name}
              </Link>
            ))}
            <Link to="/service-areas" className="inline-flex items-center gap-1 font-body font-bold text-coral px-4 py-2">See all <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* 8. TRUST / REVIEWS */}
      <section className="bg-teal text-cream py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <div className="flex justify-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-7 h-7 fill-sunny text-sunny" />)}</div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">New to junk, not to great service.</h2>
          <p className="mt-4 font-body text-lg text-cream/90 max-w-2xl mx-auto">Skedaddle comes from the crew behind Tides Team Exteriors, the Lowcountry roofing and fencing team rated 4.9 stars from over 100 Google reviews. Same local hustle, same friendly faces, now hauling your junk!</p>
          <div className="mt-8 inline-flex items-center gap-4 bg-cream text-charcoal border-2 border-charcoal rounded-2xl px-6 py-4 shadow-brut">
            <span className="font-heading font-bold text-4xl">4.9</span>
            <div className="text-left"><div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-coral text-coral" />)}</div><div className="font-body text-sm">100+ reviews for Tides Team</div></div>
          </div>
          <p className="mt-8 font-body text-cream/80">Skedaddle reviews are rolling in soon. Be one of our first!</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3"><Btn href={BRAND.reviewGoogleUrl} target="_blank" rel="noreferrer" variant="primary" size="md" data-testid="home-review-google">Leave us a Google review</Btn><Btn href="https://www.tidesteam.com" target="_blank" variant="accent" size="md">See Tides Team reviews</Btn></div>
        </div>
      </section>

      {/* 9. GIVE BACK */}
      <section className="bg-white py-20 md:py-28 border-b-2 border-charcoal">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="bg-sunny border-2 border-charcoal rounded-[2rem] shadow-brutlg p-8 md:p-14 grid md:grid-cols-[auto,1fr] gap-8 items-center">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-charcoal bg-cream flex items-center justify-center shrink-0">
              <PawPrint className="w-12 h-12 md:w-16 md:h-16 text-coral" />
            </div>
            <div>
              <p className="font-heading font-semibold text-charcoal/70 text-lg">Give back</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">Junk that does some good!</h2>
              <p className="mt-3 font-body text-charcoal/80 max-w-xl">We love our four legged neighbors, so we give back to local animal shelters. When your junk skedaddles, a little good tags along with it.</p>
              <Btn to="/give-back" variant="primary" size="md" className="mt-6">Read more</Btn>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <Reveal className="text-center">
            <p className="font-heading font-semibold text-teal text-lg">FAQ</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-1">Answers before you ask.</h2>
          </Reveal>
          <div className="mt-10">
            <Accordion type="single" collapsible className="space-y-4" data-testid="home-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white border-2 border-charcoal rounded-2xl px-5 shadow-brut">
                  <AccordionTrigger className="font-heading font-bold text-lg text-left hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="font-body text-charcoal/75 text-base">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="mt-12 text-center">
            <Btn to="/contact" variant="primary">{CTA.quote}</Btn>
          </div>
        </div>
      </section>
    </>
  );
}
